import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView } from './ChartView'
import { formatNumber } from './chartFormat'

/**
 * The company lookup chart: any U.S.-listed company's revenue, profit, cash
 * or stock buybacks each fiscal year, as reported in its annual reports on SEC
 * EDGAR. The page's own data (`chart.data`) is the featured company's revenue;
 * everything else comes from GET /companies/:ticker.json (CompaniesController).
 * `?company=MSFT&measure=profit` in the address opens a company directly, and
 * the address follows each search so a result can be shared. Every figure in
 * the table links to the filing it was reported in.
 */

const FEATURED = { ticker: 'AAPL', name: 'Apple', measure: 'revenue' } // keep in step with the chart.yml refresh options
const MEASURES = {
  revenue: { button: 'Revenue', yLabel: 'Revenue, dollars', title: (name) => `${name}'s revenue each fiscal year` },
  profit: { button: 'Profit', yLabel: 'Profit (net income), dollars', title: (name) => `${name}'s profit (net income) each fiscal year` },
  cash: { button: 'Cash', yLabel: 'Cash and cash equivalents at year end, dollars', title: (name) => `${name}'s cash at the end of each fiscal year` },
  buybacks: { button: 'Buybacks', yLabel: 'Stock bought back, dollars', title: (name) => `What ${name} spent buying back its own stock each fiscal year` },
}

async function getJson(path, signal) {
  const response = await fetch(path, { headers: { Accept: 'application/json' }, signal })
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`Request failed (${response.status})`)
  return response.json()
}

function formatDate(iso) {
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const exactDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function setAddress(ticker, measure) {
  const url = new URL(window.location.href)
  url.searchParams.set('company', ticker)
  url.searchParams.set('measure', measure)
  window.history.replaceState(null, '', url)
}

export function CompanyLookup({ chart }) {
  const inputId = useId()
  const listId = useId()
  const [result, setResult] = useState({ ...FEATURED, measures: { revenue: chart.data } })
  const [measure, setMeasure] = useState(FEATURED.measure)
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)

  const lookUp = async (raw, preferredMeasure = measure) => {
    const id = raw.trim()
    if (!id) return
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const found = await getJson(`/companies/${encodeURIComponent(id)}.json`, controller.signal)
      if (!found) {
        setStatus({
          loading: false,
          message: `No figures found for “${id}”. Try a company's stock ticker, such as MSFT, or pick a name from the list.`,
        })
        return
      }
      const chosen = MEASURES[preferredMeasure] ? preferredMeasure : 'revenue'
      setResult(found)
      setMeasure(chosen)
      setStatus({ loading: false, message: null })
      setAddress(found.ticker, chosen)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That company couldn’t be loaded from SEC EDGAR. Please try again.' })
    }
  }

  // Open the company in the address, if any.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('company')) lookUp(params.get('company'), params.get('measure'))
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Suggest companies whose ticker or name starts with what has been typed.
  useEffect(() => {
    const prefix = query.trim()
    if (prefix.length < 1) {
      setSuggestions([])
      return undefined
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      getJson(`/companies.json?q=${encodeURIComponent(prefix)}`, controller.signal)
        .then((body) => setSuggestions(body?.companies ?? []))
        .catch(() => {})
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query])

  const chooseMeasure = (value) => {
    if (!result.measures[value]) {
      lookUp(result.ticker, value) // the featured company arrives with its revenue only
      return
    }
    setMeasure(value)
    setAddress(result.ticker, value)
  }

  const meta = MEASURES[measure]
  const points = result.measures[measure] ?? []
  const latest = points[points.length - 1]

  return (
    <div className="w-full">
      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => { event.preventDefault(); lookUp(query) }}
      >
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="form-label">Find a company</label>
          <input
            id={inputId}
            type="search"
            className="input-form-field"
            placeholder="A name or ticker, e.g. Microsoft or KO"
            autoComplete="off"
            list={listId}
            value={query}
            maxLength={40}
            onChange={(event) => setQuery(event.target.value)}
          />
          <datalist id={listId}>
            {suggestions.map((company) => (
              <option key={`${company.cik}-${company.ticker}`} value={company.ticker}>{company.name}</option>
            ))}
          </datalist>
        </div>
        <button type="submit" className="btn-primary min-h-10" disabled={status.loading}>
          {status.loading ? 'Loading…' : 'Show'}
        </button>
      </form>

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-semibold text-ink break-words">
          {result.name} ({result.ticker})
          {latest ? (
            <span className="block text-sm font-normal text-ink-muted">
              Fiscal year ended {formatDate(latest.end)}: {formatNumber(latest.value, 'dollars')}.
            </span>
          ) : null}
        </p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Measure">
          {Object.entries(MEASURES).map(([value, { button }]) => (
            <button
              key={value}
              type="button"
              className={`${measure === value ? 'btn-primary' : 'btn-secondary'} btn-sm min-h-10`}
              aria-pressed={measure === value}
              disabled={status.loading}
              onClick={() => chooseMeasure(value)}
            >
              {button}
            </button>
          ))}
        </div>
      </div>

      {points.length >= 2 ? (
        <ChartView
          searchable={false}
          chart={{ ...chart, title: meta.title(result.name), y_label: meta.yLabel, data: points }}
        />
      ) : (
        <p className="alert-info" role="status">
          {result.name}'s annual reports don't give {meta.button.toLowerCase()} for {points.length ? 'more than one year' : 'any year'} in
          the form the SEC collects (XBRL). A company that files under international accounting rules, or only recently
          began filing, may not have these figures.
        </p>
      )}

      {points.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-primary">The numbers for {result.name}, with their filings</summary>
          <div className="mt-2 max-h-80 overflow-y-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th scope="col" className="text-left">Fiscal year ended</th>
                  <th scope="col" className="text-right">{meta.button}</th>
                  <th scope="col" className="text-right">Reported in</th>
                </tr>
              </thead>
              <tbody>
                {[...points].reverse().map((p) => (
                  <tr key={p.label}>
                    <td>{formatDate(p.end ?? p.label)}</td>
                    <td className="text-right tabular-nums">{exactDollars.format(p.value)}</td>
                    <td className="text-right">
                      {p.url ? (
                        <a className="table-link" href={p.url} rel="noopener" target="_blank">{p.form ?? 'Filing'}</a>
                      ) : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  )
}
